import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

export type AppRole = 'admin' | 'direction' | 'commercial' | 'production' | 'administratif' | 'lecture';

interface AuthCtx {
  user: User | null;
  session: Session | null;
  roles: AppRole[];
  loading: boolean;
  signOut: () => Promise<void>;
  hasRole: (r: AppRole) => boolean;
  isAuthorized: boolean;
  refreshRoles: () => Promise<void>;
}

const Ctx = createContext<AuthCtx | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [roles, setRoles] = useState<AppRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [rolesLoaded, setRolesLoaded] = useState(false);

  const loadRoles = async (uid: string) => {
    try {
      const { data } = await supabase.from('user_roles').select('role').eq('user_id', uid);
      setRoles((data || []).map((r: any) => r.role as AppRole));
    } finally {
      setRolesLoaded(true);
    }
  };

  useEffect(() => {
    let lastUid: string | null = null;

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, sess) => {
      setSession(sess);
      setUser(sess?.user ?? null);
      setLoading(false);
      const uid = sess?.user?.id ?? null;
      if (uid) {
        // Évite de recharger les rôles à chaque TOKEN_REFRESHED
        if (uid !== lastUid) {
          lastUid = uid;
          setRolesLoaded(false);
          setTimeout(() => loadRoles(uid), 0);
        }
      } else {
        lastUid = null;
        setRoles([]);
        setRolesLoaded(true);
      }
    });

    supabase.auth.getSession().then(({ data: { session: sess } }) => {
      setSession(sess);
      setUser(sess?.user ?? null);
      setLoading(false);
      const uid = sess?.user?.id ?? null;
      if (uid && uid !== lastUid) {
        lastUid = uid;
        loadRoles(uid);
      } else if (!uid) {
        setRolesLoaded(true);
      }
    }).catch(() => { setLoading(false); setRolesLoaded(true); });

    // Filet de sécurité : débloque l'écran de chargement seulement (ne force pas "non autorisé")
    const timeout = setTimeout(() => setLoading(false), 3000);
    return () => { subscription.unsubscribe(); clearTimeout(timeout); };

  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
    setRoles([]);
  };

  const hasRole = (r: AppRole) => roles.includes(r);
  const isAuthorized = roles.length > 0;

  const refreshRoles = async () => {
    if (user) await loadRoles(user.id);
  };

  const effectiveLoading = loading || (!!user && !rolesLoaded);

  return (
    <Ctx.Provider value={{ user, session, roles, loading: effectiveLoading, signOut, hasRole, isAuthorized, refreshRoles }}>
      {children}
    </Ctx.Provider>
  );
}

export function useAuth() {
  const c = useContext(Ctx);
  if (!c) throw new Error('useAuth must be used within AuthProvider');
  return c;
}
